import React from 'react';
import { storedSession } from '../AppShell';
import { hasPermission } from '../../utils/permissions';

// Candidate pipeline (bundle 4a-2): labels and small pieces shared by the candidate screens.

export const STAGE_LABEL = {
  sourced: 'Sourced', recruiter_screen: 'Recruiter screen', submitted: 'Submitted', ai_screen: 'Quali5d AI screen',
  hm_review: 'HM review', interview: 'Interview', offer: 'Offer', bgv: 'BGV / drug test', joined: 'Joined',
  rejected: 'Rejected', withdrawn: 'Withdrawn',
};
export const MOVE_STAGES = ['sourced', 'recruiter_screen', 'submitted', 'ai_screen', 'hm_review', 'interview', 'offer', 'bgv', 'joined'];

// RejectFlow / CandProfile (approved mockups); hm_decision is system-only (shown, never offered)
export const REJECT_REASONS = [
  ['skills_mismatch', 'Skills don’t match'], ['failed_technical', 'Couldn’t answer technical questions'],
  ['compensation', 'Compensation mismatch'], ['wont_relocate', 'Won’t relocate'], ['not_looking', 'Not looking / not in market'],
  ['communication', 'Communication skills'], ['notice_period', 'Notice period too long'], ['work_authorization', 'Work authorization'],
  ['over_qualified', 'Over-qualified'], ['under_qualified', 'Under-qualified'], ['failed_authenticity', 'Failed authenticity check'],
  ['other', 'Other…'],
];
export const REJECT_LABEL = { ...Object.fromEntries(REJECT_REASONS), hm_decision: 'Hiring manager decision (AI screen)' };
export const PENDING_REASONS = [
  ['no_answer', 'No answer'], ['emailed_no_reply', 'E-mailed, no reply'], ['call_back', 'Call back on (date + reminder)'],
  ['voicemail_left', 'Voicemail left'], ['needs_time', 'Interested, needs time'], ['waiting_documents', 'Waiting on documents'],
];
export const PENDING_LABEL = Object.fromEntries(PENDING_REASONS);
export const WITHDRAWN_REASONS = [
  ['other_offer', 'Accepted another offer'], ['counter_offer', 'Took a counter-offer'], ['compensation', 'Compensation'],
  ['role', 'The role'], ['company', 'The company'], ['location', 'Location'], ['timing', 'Timing'], ['personal', 'Personal reasons'],
  ['other', 'Other'],
];
export const WITHDRAWN_LABEL = { ...Object.fromEntries(WITHDRAWN_REASONS), not_interested: 'No longer interested' };
export const WORK_AUTH_STATUS = [
  ['citizen', 'Citizen'], ['permanent_resident', 'Permanent resident'], ['visa_transfer', 'Visa transfer'],
  ['needs_sponsorship', 'Needs sponsorship'], ['other', 'Other'],
];
export const PRIVACY_HINT = 'Job-related facts only — don’t record age, family, health, religion, or other protected traits.';

// Under every free-text box about a candidate (bundle 4c)
export function PrivacyHint() {
  return <span className="block text-[11px] text-[#64748B] mt-1" data-testid="privacy-hint">{PRIVACY_HINT}</span>;
}

// Evidence / why text as people read it: never raw JSON or field names, no doubled quotes, cut at a word boundary
// with an ellipsis — the full text on hover.
export function cleanText(raw) {
  let t = raw == null ? '' : String(raw).trim();
  if (/^[[{]/.test(t)) {
    try {
      const v = JSON.parse(t);
      const vals = Array.isArray(v) ? v : (typeof v === 'object' && v ? Object.values(v) : [v]);
      t = vals.map((x) => (x && typeof x === 'object' ? (x.evidence || x.text || '') : String(x))).filter(Boolean).join('; ');
    } catch (e) { t = t.replace(/[{}[\]"]/g, ''); }
  }
  t = t.replace(/\b[a-z]+_[a-z_]+\s*:\s*/g, '').replace(/"[A-Za-z_][\w ]{0,40}"\s*:\s*/g, '').replace(/\s+/g, ' ').trim();
  const pairs = ['""', "''", '“”', '‘’', '«»'];
  for (let i = 0; i < 2; i += 1) {            // surrounding quotes, then doubled ones (""x"" → "x"), then surrounding again
    while (t.length >= 2 && pairs.includes(t[0] + t[t.length - 1])) t = t.slice(1, -1).trim();
    t = t.replace(/(["“”])\1+/g, '$1');
  }
  const n = (ch) => t.split(ch).length - 1;
  if (n('"') % 2 || n('“') !== n('”')) {      // a lone quote left by a cut ("Owns test…)
    t = /^["“”«»]/.test(t) ? t.replace(/^["“”«»]+\s*/, '') : t.replace(/\s*["“”«»]+(?=…?$)/, '');
  }
  return t.trim();
}

export function truncateWords(t, max) {
  if (!t || t.length <= max) return t;
  const cut = t.slice(0, max + 1);
  const at = cut.lastIndexOf(' ');
  return `${(at > max * 0.5 ? cut.slice(0, at) : t.slice(0, max)).replace(/[\s,;:.–—-]+$/, '')}…`;
}

export function Evidence({ text, max = 160, quote = true, className = '' }) {
  const full = cleanText(text);
  if (!full) return null;
  const short = truncateWords(full, max);
  const Tag = quote ? 'q' : 'span';
  return <Tag className={className} title={short !== full ? full : undefined} data-testid="evidence">{short}</Tag>;
}
export const SOURCE_LABEL = {
  bulk_import: 'Bulk import', careers_page: 'Careers page', email_in: 'E-mail', referral: 'Referral', manual: 'Added by hand',
  phase3_backfill: 'Quali5d AI screen', job_board: 'Job board', licensed: 'Licensed', synthetic: 'Synthetic (test)',
};
export const WORK_AUTH = [
  ['citizen', 'Citizen'], ['permanent_resident', 'Permanent resident'], ['visa', 'Visa'], ['needs_sponsorship', 'Needs sponsorship'],
  ['unknown', 'Unknown'],
];

// Admins and people with Recruit see the candidate database
export function canUseCandidates(user) {
  const u = user || storedSession().user;
  return !!u && (u.role === 'company_super_admin' || hasPermission(u, 'recruit'));
}
export const isAdmin = (user) => (user || storedSession().user).role === 'company_super_admin';

export function StagePill({ stage, status, round }) {
  const tone = status === 'rejected' || status === 'withdrawn' ? 'bg-[#FEE2E2] text-[#991B1B]'
    : status === 'pending' ? 'bg-[#FEF3C7] text-[#92400E]' : status === 'joined' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#E8F1FB] text-[#003D82]';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${tone}`} data-testid="stage-pill">
      {STAGE_LABEL[stage] || stage}{stage === 'interview' && round ? ` ${round}` : ''}{status === 'pending' ? ' · pending' : ''}
    </span>
  );
}

export function Chip({ children, tone = 'plain', title }) {
  const cls = { plain: 'bg-[#F1F5F9] text-[#334155]', must: 'bg-[#EEF2FF] text-[#3730A3]', yes: 'bg-[#DCFCE7] text-[#166534]',
    no: 'bg-[#F1F5F9] text-[#94A3B8]', warn: 'bg-[#FEF3C7] text-[#92400E]', bad: 'bg-[#FEE2E2] text-[#991B1B]' }[tone];
  return <span title={title} className={`inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[11.5px] font-semibold ${cls}`}>{children}</span>;
}

export function FlagBadge({ count }) {
  if (!count) return null;
  return <Chip tone="bad" title="Shares a phone, e-mail or resume with a differently named person">▲ Review: shared contact / resume</Chip>;
}

export function DuplicateBadges({ tiers }) {
  if (!tiers || !tiers.length) return null;
  return tiers.map((t) => <Chip key={t} tone="warn" title="Possible duplicate — see Duplicates">{t === 'likely' ? 'Likely duplicate' : 'Possible duplicate'}</Chip>);
}

export const fmtDate = (iso) => (iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
export const fmtDateTime = (iso) => (iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
export const place = (c) => [c.city, c.state, c.country].filter(Boolean).join(', ');
